import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/apec — APEC Business Travel Card (EN mirror)
import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { APEC_PROVINCES } from "@/data/apec-provinces";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

const TITLE = "APEC Business Travel Card (ABTC) — Full-Service Application | NYC";
const DESC = "End-to-end APEC Business Travel Card (ABTC) service for Thai business travelers. Free consultation — message us and get a same-day answer.";

const steps = [
  { name: "Free consult + eligibility check", text: "We verify your eligibility (director, executive, or shareholder ≥5% of a Thai-registered company) and prepare a document plan per MFA Thailand guidelines." },
  { name: "Document preparation", text: "Company affidavit (≤6 months), passport, 2-inch photo, APEC-1 form, business letter explaining travel necessity across APEC." },
  { name: "Filing at MFA Consular Department", text: "We submit at the Passport Division, MFA Chaengwattana, including preliminary background screening." },
  { name: "Pre-clearance across 21 economies", text: "We track pre-clearance results from every APEC member (3-4 months) via the MFA tracking system." },
  { name: "Card delivery", text: "5-year ABTC delivered via EMS Thailand-wide, with a usage guide for APEC Lane at airports across all 21 economies." },
];

const faqs = [
  { q: "How many countries does the APEC Card cover?", a: "21 economies: Australia, Brunei, Canada, Chile, China, Hong Kong, Indonesia, Japan, South Korea, Malaysia, Mexico, New Zealand, Papua New Guinea, Peru, Philippines, Russia, Singapore, Taiwan, Thailand, USA (transit/lane only), and Vietnam." },
  { q: "How long can I stay per visit?", a: "Stay length is set by each receiving APEC economy and typically ranges from 60 to 90 days per entry (e.g. 90 days Japan/Korea, 60 days Vietnam/Indonesia). The ABTC itself carries a 5-year validity with unlimited multi-entry across all 19 fully participating economies, eliminating per-trip visa applications and embassy queues." },
  { q: "Who is eligible?", a: "Thai citizens aged 18+ who are directors, senior executives, or shareholders holding ≥5% of a Thai-registered company (DBD certificate required), with documented and recurring business travel to APEC member economies. Civil servants and SOE board members on official business also qualify. NYC pre-screens eligibility under MFA Regulation B.E.2552 before filing." },
  { q: "How long does it take?", a: "Approximately 4–6 months from MFA filing, including parallel pre-clearance from each of the 19 fully participating economies (Japan, Korea, China, Australia, NZ, ASEAN, Mexico, Peru, Chile, PNG, Russia). USA and Canada grant transitional members access only — APEC Lane priority at immigration, not a pre-cleared visa." },
  { q: "What are the government fees?", a: "MFA Department of Consular Affairs fee is THB 5,000 for a 5-year ABTC, payable on filing at Chaeng Watthana. NYC concierge service from THB 12,000 covers document compilation, MFA filing, translation of corporate evidence, status monitoring across 19 economies, and final card collection — quoted within 15 minutes via LINE @nycli." },
  { q: "How do I renew?", a: "File a renewal application 3–6 months before the 5-year expiry — the same MFA process and economy pre-clearances apply. NYC maintains a calendar reminder system and contacts each cardholder 180, 90 and 30 days before expiry, then re-files using your existing on-file corporate documents to cut prep time roughly in half." },
  { q: "Is the card still valid if I change employer?", a: "Yes, the ABTC remains valid until its printed expiry date even if you change employer. However, you must notify MFA in writing within 30 days under MFA Notification B.E.2552 §8 — failure to report a material change in company affiliation can lead to revocation and a 5-year re-application bar. NYC files the change notice for THB 3,500." },
  { q: "What if I lose the card?", a: "File a police report (บันทึกประจำวัน) at any Thai police station within 24 hours, then submit a replacement application at MFA Consular Affairs with the report, ID copy, and current DBD certificate. NYC handles replacement filing, MFA queue, and courier delivery within 7 Thai business days; emergency travel letters can be issued by MFA in parallel." },
];

export const Route = createFileRoute("/en/apec/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("apec", "en", "https://nyclanguageinstitute.com/en/apec") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/en/apec`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/apec", "en")}, { name: "description", content: ctaDesc(DESC, "/en/apec", "en")},
        { name: "keywords", content: "APEC Card, ABTC, APEC Business Travel Card, Thailand APEC, APEC Lane, business travel Asia Pacific" },
        { property: "og:title", content: ctaTitle(TITLE, "/en/apec", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/apec", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/apec` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/apec` },
      ],
      scripts: [
        ...buildLandingSchemas({
          path: "/en/apec", title: TITLE, description: DESC, serviceType: "APEC Business Travel Card Service", priceFromThb: 12000,
          steps, faqs,
          breadcrumbs: [{ name: "Home", path: "/en" }, { name: "Services", path: "/en/services" }, { name: "APEC Card", path: "/en/apec" }],
        }),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing APEC ABTC application tooling for 21 economies." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: ApecHubEn,
});

function ApecHubEn() {
  return (
    <>
      <ServiceLanding
        locale="en"
        badge="APEC Business Travel Card"
        title="APEC Card (ABTC) — Visa-Free Travel to 21 Economies"
        subtitle={DESC}
        heroBullets={[
          "Enter 21 economies without separate visas",
          "Dedicated APEC Lane at participating airports",
          "5-year card validity, unlimited entries",
          "NYC handles full MFA Consular filing",
        ]}
        whoFor={{
          th: [
            "Executives and directors of Thai-registered companies frequently traveling across APEC",
            "Importers and exporters operating in the Asia-Pacific region",
            "Startup founders pitching in Singapore, Hong Kong, or Taiwan",
          ],
          foreign: [
            "Foreign executives of Thai-registered companies who meet Thai-citizenship eligibility",
            "Dual-nationals using a Thai passport for APEC business travel",
          ],
        }}
        steps={steps}
        pricing={[
          { label: "MFA government fee", price: "5,000", note: "5-year card validity" },
          { label: "NYC service — Standard", price: "12,000", note: "Filing + full tracking" },
          { label: "Premium (rush + courier pickup)", price: "18,000", note: "Includes dedicated coordinator" },
        ]}
        faqs={faqs}
        relatedLinks={[
          { to: "/en/visa", label: "Worldwide Visa Service" },
          { to: "/en/translate/mfa", label: "Translation + MFA Legalization" },
          { to: "/en/police-clearance", label: "Police Clearance" },
          { to: "/en/embassy", label: "Embassy Legalization" },
          { to: "/en/notary", label: "Notary Public" },
          { to: "/blog/apec-business-travel-card-21-economies", label: "Blog: APEC Card explained" },
        ]}
      />
      <section className="mx-auto max-w-6xl px-4 py-10">
        <h2 className="text-2xl font-semibold mb-2">APEC Card by Province / City</h2>
        <p className="text-muted-foreground mb-6">Document pickup and on-the-ground service across Thailand's primary business hubs. We file every APEC application at MFA Bangkok — no Bangkok trip required from your province.</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {APEC_PROVINCES.map((p) => (
            <li key={p.slug}>
              <Link
                to="/en/apec/$province"
                params={{ province: p.slug }}
                className="block rounded-lg border border-border p-4 hover:border-primary hover:bg-accent transition-colors"
              >
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.industryFocus}</div>
                <div className="text-xs text-muted-foreground mt-1">Corridors: {p.apecCorridor}</div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <RelatedHubGrid locale="en" />
    </>

  );
}

